import React, { useEffect, useState } from 'react';
import api from '../api';
import DrillDownModal from '../components/DrillDownModal';
import { Target, CheckCircle, Clock, Warning, ArrowClockwise, Crosshair } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const STATUS_COLORS = { 'Live': '#2D7D46', 'In Progress': '#D9943B', 'Blocked': '#C6493A', 'Planned': '#87A98E' };

function ProgressBar({ pct, status }) {
  const color = pct >= 90 ? '#2D7D46' : pct >= 60 ? '#D9943B' : '#C6493A';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-surface-muted rounded-full h-2 min-w-[60px]">
        <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
      <span className="text-[10px] font-mono font-medium" style={{ color }}>{pct}%</span>
    </div>
  );
}

export default function StrategyTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);

  useEffect(() => {
    api.get('/api/dashboard/strategy').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse text-content-secondary">Loading strategy data...</div>;
  if (!data) return <div className="text-status-alert">Failed to load</div>;

  const { initiatives } = data;
  const avgProgress = Math.round(initiatives.reduce((a, i) => a + (i.progress_pct || 0), 0) / Math.max(initiatives.length, 1));
  const live = initiatives.filter(i => i.status === 'Live').length;
  const inProgress = initiatives.filter(i => i.status === 'In Progress').length;
  const blocked = initiatives.filter(i => i.blocker_reason).length;

  // By theme
  const themes = {};
  initiatives.forEach(i => { themes[i.strategic_theme] = (themes[i.strategic_theme] || 0) + 1; });
  const themePie = Object.entries(themes).map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 25) + '...' : name, value }));
  const COLORS = ['#0A4D3C', '#C2714F', '#D9943B', '#87A98E'];

  // By outcome
  const outcomeData = {};
  initiatives.forEach(i => {
    const oc = i.desired_outcome || 'Unmapped';
    if (!outcomeData[oc]) outcomeData[oc] = { count: 0, avgProg: 0, total: 0 };
    outcomeData[oc].count++;
    outcomeData[oc].total += i.progress_pct || 0;
    outcomeData[oc].avgProg = Math.round(outcomeData[oc].total / outcomeData[oc].count);
  });
  const outcomeChart = Object.entries(outcomeData).map(([name, v]) => ({ name: name.length > 20 ? name.slice(0, 20) + '...' : name, progress: v.avgProg, count: v.count }));

  return (
    <div data-testid="strategy-tracker" className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Agenda & Strategy Tracker</h1>
        <p className="text-sm text-content-secondary mt-1">Monitor SCB 2022 foundations to 2023 inclusive sustainability agenda</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Target, label: 'Avg Progress', val: `${avgProgress}%`, color: 'text-brand-primary', dk: 'strategy_avg_progress' },
          { icon: CheckCircle, label: 'Live', val: live, color: 'text-status-on-track', dk: 'strategy_live' },
          { icon: Clock, label: 'In Progress', val: inProgress, color: 'text-status-watch', dk: 'strategy_in_progress' },
          { icon: Warning, label: 'Blocked', val: blocked, color: 'text-status-alert', dk: 'strategy_blocked' },
        ].map((k, i) => (
          <div key={i} onClick={() => setDrillKey(k.dk)} className="bg-white border border-border-default rounded-lg p-3.5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all group">
            <k.icon size={16} className={`${k.color} mb-1.5`} />
            <p className={`text-xl font-heading font-bold ${k.color}`}>{k.val}</p>
            <p className="text-[10px] text-content-secondary font-medium">{k.label}</p>
            <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">Click to drill down</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">By Strategic Theme</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={themePie} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                {themePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Progress by Outcome</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={outcomeChart} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#49564C' }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#49564C' }} width={110} />
              <Tooltip />
              <Bar dataKey="progress" radius={[0, 3, 3, 0]}>
                {outcomeChart.map((e, i) => <Cell key={i} fill={e.progress >= 80 ? '#2D7D46' : e.progress >= 50 ? '#D9943B' : '#C6493A'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Initiatives Table */}
      <div className="bg-white border border-border-default rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border-default">
          <h3 className="font-heading font-semibold text-sm text-content-primary">Initiative Register</h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="strategy-table" className="w-full text-[12px]">
            <thead>
              <tr className="bg-surface-muted">
                {['ID', 'Initiative', 'Framework', 'Theme', 'Owner', 'Status', 'Progress', 'Target Date', 'Blocker'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {initiatives.map((i, idx) => (
                <tr key={idx} className="hover:bg-surface-muted/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-[10px] font-medium text-brand-primary">{i.initiative_id}</td>
                  <td className="px-4 py-2.5 font-medium text-content-primary max-w-[200px]">{i.title}</td>
                  <td className="px-4 py-2.5 text-content-secondary"><span className="px-1.5 py-0.5 bg-brand-primary/5 text-brand-primary rounded text-[9px] font-medium">{i.source_framework}</span></td>
                  <td className="px-4 py-2.5 text-content-secondary text-[10px]">{i.strategic_theme}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{i.owner}</td>
                  <td className="px-4 py-2.5"><span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: `${STATUS_COLORS[i.status] || '#87A98E'}15`, color: STATUS_COLORS[i.status] || '#87A98E' }}>{i.status}</span></td>
                  <td className="px-4 py-2.5 min-w-[120px]"><ProgressBar pct={i.progress_pct} /></td>
                  <td className="px-4 py-2.5 font-mono text-[10px]">{i.target_date}</td>
                  <td className="px-4 py-2.5 text-status-alert text-[10px]">{i.blocker_reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
